// Desktop sidebar (≥ 1024 px) and tablet rail (768–1023 px), IA §2.1/§2.3. Only items the role can
// access are rendered. The active group is expanded; others expand on demand. Desktop layout per
// D-038: the store switcher heads the sidebar, main links come first with no heading, then "Customizations".
// Rows follow the reference: 33 px, 12 px medium labels, 16 px icons 16 px from the label; every
// row (sub-items too) carries its own icon, #495057 at rest (owner, 2026-11-01: `text-fg-nav` for
// all sidebar text and icons) and the logo gradient when active.
import { Fragment, useState } from "react";
import { useRouterState } from "@tanstack/react-router";
import { t } from "@ady/i18n";
import { BrandIcon, cx, Icon, Logo, VerifiedTick } from "@ady/ui";
import { TID } from "../testids";
import { AppLink } from "./AppLink";
import { SECTION_BARS, tabRow } from "./GroupBar";
import { useShell } from "./context";
import { PausedBadge, PlanLockBadge, usePausedNav, usePlanLocks } from "./ControlCentre";
import { hrefFor, iconFor, itemForPathname, navKey, visibleNav, type NavGroup } from "./nav";
import { GoSpan, VerifiedMark, verificationHref } from "./VerifiedMark";

/** Groups listed under "Customizations"; everything before them is the main links (no heading). */
const CUSTOMIZE = new Set(["store", "templates", "apps", "settings", "help"]);
/** Groups listed last, under "Adyvance Services" (D-046). */
const SERVICES = new Set([
  "importMarketplace",
  "smsService",
  "digitalMarketing",
  "designContent",
  "growthStrategy",
  "courses",
]);

/** Service pages kept (Home quick links) but not listed in the sidebar (owner, 2026-10-21). */
const NOT_IN_SIDEBAR = new Set(["designContent", "growthStrategy"]);

function StoreBox() {
  const { store, membership, orgSlug, storeSlug, openStoreSwitcher } = useShell();
  return (
    <button
      type="button"
      onClick={openStoreSwitcher}
      aria-label={t("shell.storeSwitcher.label", { store: store.name, org: membership.org.name })}
      className="hidden h-18 w-full flex-none items-center gap-3 border-b border-subtle px-4 text-start hover:bg-hover lg:flex"
    >
      <span
        className="flex size-9 flex-none items-center justify-center rounded-full ady-bg-brand text-sm font-semibold text-on-primary"
        aria-hidden="true"
        data-content="user"
      >
        {Array.from(store.name)[0]?.toUpperCase()}
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="flex min-w-0 items-center gap-1">
          <span className="truncate text-sm font-medium text-fg-nav" data-content="user">
            {store.name}
          </span>
          {membership.org.verified ? <VerifiedMark verified /> : null}
          {membership.org.verified === false ? (
            <GoSpan href={verificationHref(orgSlug, storeSlug)} className="flex flex-none">
              <VerifiedTick label={t("shell.unverified")} unverified />
            </GoSpan>
          ) : null}
        </span>
        <span className="truncate text-2xs text-fg-nav uppercase">{membership.role.name}</span>
      </span>
      <Icon name="chevronRight" size={16} className="flex-none text-fg-nav" />
    </button>
  );
}

/** A group's icon, or the service's own mark (Daraz) drawn in the same colours (owner comment c8). */
function GroupIcon({
  group,
  active,
  className,
}: {
  group: NavGroup;
  active: boolean;
  className?: string;
}) {
  if (group.brand)
    return (
      <BrandIcon
        brand={group.brand}
        size={16}
        mono
        active={active}
        className={cx("size-(--ady-icon-size) flex-none", className)}
      />
    );
  return <Icon name={group.icon} gradient={active} className={className} />;
}

function SectionLabel({ children }: { children: string }) {
  return (
    <li
      aria-hidden="true"
      className="hidden px-2 pt-3 pb-0.5 text-xs font-medium text-fg-nav first:pt-2 lg:block"
    >
      {children}
    </li>
  );
}

export function Sidebar() {
  const { membership, orgSlug, storeSlug } = useShell();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Main links first (no heading), then the customization groups, each under one label.
  // Sell lives in the header POS mode menu (owner, 2026-10-21), not in the sidebar.
  const all = visibleNav(membership).filter((g) => g.id !== "sell" && !NOT_IN_SIDEBAR.has(g.id));
  const ordered = [
    ...all.filter((g) => !CUSTOMIZE.has(g.id) && !SERVICES.has(g.id)),
    ...all.filter((g) => CUSTOMIZE.has(g.id)),
    ...all.filter((g) => SERVICES.has(g.id)),
  ];
  // Items a section bar promotes (Categories, Inventory) become top-level links
  // right after their group (owner, 2026-10-21).
  const groups = ordered.flatMap((g) => {
    const promote = SECTION_BARS[g.id]?.promote ?? [];
    if (!promote.length) return [g];
    const lifted = g.items.filter((i) => promote.includes(i.id));
    return [
      { ...g, items: g.items.filter((i) => !promote.includes(i.id)) },
      ...lifted.map((i) => ({ id: i.id, label: i.label, icon: iconFor(i), items: [i] })),
    ];
  });
  // A page that lives in a section bar highlights the bar's first item (Products) in the sidebar.
  const currentId = itemForPathname(pathname, orgSlug, storeSlug)?.id;
  const activeGroup =
    groups.find((g) => g.items.some((i) => i.id === currentId))?.id ??
    groups.find((g) => g.items.some((i) => pathname.startsWith(hrefFor(i, orgSlug, storeSlug))))
      ?.id;
  const barOf = (gid: string) => SECTION_BARS[gid];
  const sidebarItems = (g: (typeof groups)[number]) => {
    const bar = barOf(g.id);
    if (!bar) return g.items;
    const kept = g.items.filter((i) => bar.sidebar.includes(i.id));
    return kept.length ? kept : g.items.slice(0, 1);
  };
  const selected = (gid: string, id: string, href: string) => {
    const bar = barOf(gid);
    if (pathname === href) return true;
    return Boolean(
      bar && currentId && !bar.sidebar.includes(currentId) && tabRow(bar, currentId)?.[0] === id,
    );
  };
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const isOpen = (id: string) => open[id] ?? id === activeGroup;
  // D-065: a feature the plan turns off carries the icon of the plan that has it.
  const lockFor = usePlanLocks();
  const pausedFor = usePausedNav();

  return (
    <nav
      aria-label={t("shell.mainNav")}
      data-testid={TID.sidebar}
      className="sticky top-0 hidden h-dvh flex-none flex-col border-e border-subtle bg-surface md:flex md:w-24 lg:w-52"
    >
      <div className="flex h-16 flex-none items-center px-4 lg:hidden">
        <Logo variant="mark" height={28} />
      </div>
      <StoreBox />
      <ul className="ady-font-inter ady-scroll-quiet flex flex-1 flex-col gap-0.5 overflow-y-auto px-2 pb-3 lg:gap-0 lg:px-2">
        {groups.map((g, gi) => {
          const items = sidebarItems(g);
          const first = items[0];
          if (!first) return null;
          // Groups with a section bar and no sidebar items (Orders) are one link; their pages are tabs.
          const single = items.length === 1;
          const active = g.id === activeGroup;
          const groupClass = cx(
            // Menu rows: Inter 12 px, weight 600 on desktop (owner, 2026-10-21).
            "flex w-full items-center gap-3 rounded-sm text-md font-medium transition-colors duration-(--ady-dur-quick) lg:font-semibold",
            "min-h-11 flex-col justify-center px-1 py-1.5 text-2xs md:text-xs lg:min-h-0 lg:flex-row lg:justify-start lg:gap-3 lg:rounded-xs lg:px-2 lg:py-1.5 lg:text-xs",
            active ? "ady-bg-brand-tint text-fg" : "ady-hover-brand text-fg-nav",
          );
          // The first group needs no heading (owner, 2026-10-21); "Customizations" starts the store tools.
          const prev = groups[gi - 1]?.id ?? "";
          const section =
            gi > 0 && CUSTOMIZE.has(g.id) && !CUSTOMIZE.has(prev)
              ? t("shell.navSections.customize")
              : SERVICES.has(g.id) && !SERVICES.has(prev)
                ? t("shell.navSections.services")
                : null;
          return (
            <Fragment key={g.id}>
              {section ? <SectionLabel>{section}</SectionLabel> : null}
              <li data-testid={TID.navGroup(g.id)}>
                {single ? (
                  <AppLink
                    href={hrefFor(first, orgSlug, storeSlug)}
                    className={groupClass}
                    data-testid={TID.navItem(navKey(first))}
                    aria-current={active ? "page" : undefined}
                  >
                    <GroupIcon
                      group={g}
                      active={active}
                      className={cx("flex-none lg:size-4", !active && "text-fg-nav")}
                    />
                    <span
                      className={cx(
                        "ady-hover-label text-center lg:text-start",
                        active && "ady-text-brand",
                      )}
                    >
                      {t(g.label)}
                    </span>
                    {pausedFor(first.id) ? (
                      <span className="hidden lg:contents">
                        <PausedBadge />
                      </span>
                    ) : lockFor(first.id) ? (
                      <span className="hidden lg:contents">
                        <PlanLockBadge unlock={lockFor(first.id)!.unlock} />
                      </span>
                    ) : null}
                  </AppLink>
                ) : (
                  <>
                    {/* Rail: the group links to its first area. Full sidebar: a disclosure. */}
                    <AppLink
                      href={hrefFor(first, orgSlug, storeSlug)}
                      className={cx(groupClass, "lg:hidden")}
                    >
                      <GroupIcon group={g} active={active} className="flex-none" />
                      <span
                        className={cx("ady-hover-label text-center", active && "ady-text-brand")}
                      >
                        {t(g.label)}
                      </span>
                    </AppLink>
                    <button
                      type="button"
                      className={cx(groupClass, "hidden lg:flex")}
                      aria-expanded={isOpen(g.id)}
                      aria-controls={`nav-list-${g.id}`}
                      onClick={() => setOpen((o) => ({ ...o, [g.id]: !isOpen(g.id) }))}
                    >
                      <GroupIcon
                        group={g}
                        active={active}
                        className={cx("flex-none lg:size-4", !active && "text-fg-nav")}
                      />
                      <span
                        className={cx(
                          "ady-hover-label flex-1 text-start",
                          active && "ady-text-brand",
                        )}
                      >
                        {t(g.label)}
                      </span>
                      <Icon
                        name={isOpen(g.id) ? "chevronDown" : "chevronRight"}
                        size={16}
                        className="text-fg-nav"
                      />
                    </button>
                    {/* Always in the DOM (collapsed = `hidden`), so every permitted item and its
                      nav-<key> test ID exist exactly once whatever the group state. */}
                    <ul
                      id={`nav-list-${g.id}`}
                      hidden={!isOpen(g.id)}
                      className="ms-4 hidden flex-col gap-px border-s border-subtle py-0.5 ps-1.5 lg:flex"
                    >
                      {items.map((item) => {
                        const href = hrefFor(item, orgSlug, storeSlug);
                        const current = selected(g.id, item.id, href);
                        return (
                          <li key={item.id}>
                            <AppLink
                              href={href}
                              data-testid={TID.navItem(navKey(item))}
                              aria-current={current ? "page" : undefined}
                              className={cx(
                                "flex min-h-control items-center gap-3 rounded-xs px-2 py-1.5 text-xs font-medium lg:min-h-0 lg:py-1 lg:font-semibold",
                                current ? "ady-bg-brand-tint" : "ady-hover-brand text-fg-nav",
                              )}
                            >
                              <Icon
                                name={iconFor(item)}
                                size={15}
                                gradient={current}
                                className={cx("flex-none", !current && "text-fg-nav")}
                              />
                              <span
                                className={cx(
                                  "ady-hover-label truncate",
                                  current && "ady-text-brand",
                                )}
                              >
                                {t(item.label)}
                              </span>
                              {pausedFor(item.id) ? (
                                <PausedBadge />
                              ) : lockFor(item.id) ? (
                                <PlanLockBadge unlock={lockFor(item.id)!.unlock} />
                              ) : null}
                            </AppLink>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </li>
            </Fragment>
          );
        })}
      </ul>
    </nav>
  );
}
